import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
// import { formatMoney } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );

            this.dispatchEvent(
              new CustomEvent('variant:change', {
                bubbles: true,
                detail: {
                  variant: JSON.parse(textContent)
                }
              })
            );

            /* code added by RIM for discounted price -  start */
            // 💰 Universal formatter
            // function formatShopifyMoney(amountInCents) {
            //   const activeCurrency = window.Shopify?.currency?.active || "USD";
            //   const rate = parseFloat(window.Shopify?.currency?.rate || 1);
            //   const converted = (amountInCents * rate) / 100;

            //   return `${new Intl.NumberFormat("en", {
            //     style: "currency",
            //     currency: activeCurrency,
            //     minimumFractionDigits: 2,
            //     maximumFractionDigits: 2
            //   }).format(converted)} ${activeCurrency}`;
            // }

            function formatMoney(cents, format) {
              if (typeof cents == "string") {
                  cents = cents.replace(".", "")
              }
              var value = "";
              var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

              var formatString = format || this.money_format;
              function defaultOption(opt, def) {
                  return typeof opt == "undefined" ? def : opt
              }
              function formatWithDelimiters(number, precision, thousands, decimal) {
                  precision = defaultOption(precision, 2);
                  thousands = defaultOption(thousands, ",");
                  decimal = defaultOption(decimal, ".");
                  if (isNaN(number) || number == null) {
                      return 0
                  }
                  number = (number / 100).toFixed(precision);
                  var parts = number.split(".")
                    , dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands)
                    , cents2 = parts[1] ? decimal + parts[1] : "";
                  return dollars + cents2
              }
              switch (format) {
              case "amount":
                  value = formatWithDelimiters(cents, 2);
                  break;
              case "amount_no_decimals":
                  value = formatWithDelimiters(cents, 0);
                  break;
              case "amount_with_comma_separator":
                  value = formatWithDelimiters(cents, 2, ".", ",");
                  break;
              case "amount_with_space_separator":
                  value = formatWithDelimiters(cents, 2, " ", ",");
                  break;
              case "amount_with_period_and_space_separator":
                  value = formatWithDelimiters(cents, 2, " ", ".");
                  break;
              case "amount_no_decimals_with_comma_separator":
                  value = formatWithDelimiters(cents, 0, ".", ",");
                  break;
              case "amount_no_decimals_with_space_separator":
                  value = formatWithDelimiters(cents, 0, " ");
                  break;
              case "amount_with_apostrophe_separator":
                  value = formatWithDelimiters(cents, 2, "'", ".");
                  break;
              default:
                value = formatWithDelimiters(cents, 2);
                break;
              }
              return value;
            }

            function updateDiscountTable(curVariant) {
                const $table = $(".hulkapps-table");
                window.discountData = {
                  singlePrice: curVariant.price,
                  quantityDiscountMap: {
                    1: 0,
                  },
                };
                $table.each(function () {
                  const $rows = $(this).find("tbody tr");
                  $rows.each(function () {
                    const $cells = $(this).find(
                      "td:nth-child(1),td:nth-child(2),td:nth-child(3)"
                    );
                    const quantity = parseInt($($cells[0]).html());
                    const discount = parseInt($($cells[1]).html()) / 100;
                    const price = curVariant.price - curVariant.price * discount;
                    
                    window.discountData.quantityDiscountMap[quantity] = discount;
                    $($cells[2]).html(formatMoney(price.toString(), "amount"));
                  });
                });
                

                const $input = $(`input#Quantity[data-product-id="${curVariant.featured_image.product_id}"]`);
                console.log("herer --->", curVariant.inventory_quantity, curVariant, $input);
                if (curVariant.inventory_policy != "continue"){
                  $input.attr("max", curVariant.inventory_quantity);
                } else {
                  $input.attr("max", 1000);
                }
                

                //$("#Quantity").attr("max", curVariant.inventory_quantity);


                // 1️⃣ Limit the scope to the correct product-form-component
                // const $component = $(`quantity-selector-component[data-product-id="${curVariant.featured_image.product_id}"]`);

                // console.log("component --->", $component);

                // // 2️⃣ Update ONLY that component’s Quantity max
                // $component.find('input#Quantity').attr("max", curVariant.inventory_quantity);

                updateDiscountQuantityPrice();
            }

            setTimeout(() => {
              updateDiscountTable(JSON.parse(textContent));
            }, 300);

            function updateDiscountQuantityPrice() {
              if (!window.discountData) return;

              const { singlePrice, quantityDiscountMap } = window.discountData;

              const quantityVal = +$("#Quantity").val();
              
              if (!quantityVal) return;

              const thresholdQuantity = Object.keys(quantityDiscountMap)
                .reverse()
                .find((q, index, quantities) => {
                  const prevThreshold = quantities[index + 1]
                    ? quantities[index + 1]
                    : quantities[index];
                  return quantityVal >= +q && quantityVal >= +prevThreshold;
                });
              const discount = quantityDiscountMap[thresholdQuantity];
              const discountedPrice = singlePrice - singlePrice * discount;
              const originalPrice = singlePrice;
              const originalPriceHtml = `<s>${formatMoney(originalPrice.toString(), "amount")} ${Shopify.currency.active}</s>`;
              $("[data-product-price]").html(`	
                ${discountedPrice !== originalPrice ? originalPriceHtml : ""}	
                ${formatMoney(discountedPrice.toString(), "amount")} ${Shopify.currency.active}` );
              
            }
            /* code added by RIM for discounted price -  end */
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}


document.addEventListener('variant:change', (event) => {
  console.log("here in this");
  const variant = event.detail.variant;
  let mediaId = variant.featured_media?.id;

  // fallback for monogram variants that reuse color media
  if (!mediaId || document.querySelector(`[data-media-id="${mediaId}"]`) === null) {
    mediaId = variant.featured_image?.id; // fallback to variant featured image
  }

  const gallery = document.querySelector('[data-testid="product-information-media"]');
  if (!gallery) return;

  gallery.querySelectorAll('.product-media').forEach((media) => {
    console.log("media.dataset.mediaId --->", media.dataset.mediaId, mediaId);
    media.classList.toggle('is-active', media.dataset.mediaId == mediaId);
  });
});

